import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authApi } from "@/api/auth.api";
import { rolesApi } from "@/api/roles.api";
import { queryKeys } from "@/utils/queryKeys";

export function useAdmins() {
  return useQuery({
    queryKey: queryKeys.team.admins(),
    queryFn: authApi.listAdmins,
  });
}

export function useCreateAdmin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; email: string; password: string; roleId: string }) =>
      authApi.createAdmin(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.team.admins() }),
  });
}

export function useUpdateAdmin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string; roleId?: string; active?: boolean }) =>
      authApi.updateAdmin(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.team.admins() }),
  });
}

export function useDeleteAdmin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => authApi.deleteAdmin(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.team.admins() }),
  });
}

/** Every SUPER_ADMIN-defined role. */
export function useRoles() {
  return useQuery({
    queryKey: queryKeys.team.roles(),
    queryFn: rolesApi.list,
  });
}

export function useCreateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; pages: string[] }) => rolesApi.create(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.team.roles() }),
  });
}

export function useUpdateRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...payload
    }: {
      id: string;
      name?: string;
      pages?: string[];
      active?: boolean;
    }) => rolesApi.update(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.team.roles() });
      // A role's own admins may now see different pages, and its name may show elsewhere.
      queryClient.invalidateQueries({ queryKey: queryKeys.team.admins() });
      queryClient.invalidateQueries({ queryKey: queryKeys.team.myPageAccess() });
    },
  });
}

export function useDeleteRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => rolesApi.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.team.roles() }),
  });
}

/** Which pages the calling admin's own sidebar shows — read by Sidebar/RoleGuard, not just the Team pages. */
export function useMyPageAccess() {
  return useQuery({
    queryKey: queryKeys.team.myPageAccess(),
    queryFn: rolesApi.myPageAccess,
    staleTime: 60_000,
  });
}
